<script setup>
import { Link, router } from "@inertiajs/vue3";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import FollowButton from "@/Components/Profile/FollowButton.vue";

/**
 * About page team card (GH-1145, Figma "Friend"): gradient header with a
 * LinkedIn "Connect" link and the member's handle, a circular headshot
 * straddling the header's bottom edge, name/title/bio, and Visit + Follow.
 * The handle (linked to the profile) and the buttons only render when the
 * member has a Gift Hero profile (see config/gifthero.php `team`). A Gift
 * Hero viewing their own card gets a full-width Visit and no Follow, as on
 * ProfileHero — you can't follow yourself.
 */
const props = defineProps({
    member: {
        type: Object,
        required: true,
    },
});

function visitProfile() {
    router.get(route("user.giftlists", props.member.profile.username));
}
</script>

<template>
    <article
        class="flex flex-col rounded-brand border border-brand-tan bg-white p-[25px]"
    >
        <div
            class="relative h-[109px] rounded-[4px] bg-gradient-to-l from-brand-red to-[#ffb545]"
        >
            <a
                v-if="member.linkedIn"
                :href="member.linkedIn"
                target="_blank"
                rel="noopener"
                class="absolute left-[13px] top-[13px] flex items-center gap-[7px] text-white transition hover:text-white/70"
            >
                <img
                    :src="asset('images/brand/icon-linkedin.svg')"
                    alt=""
                    class="size-5"
                />
                <span class="text-[13px] font-semibold leading-[1.2]">
                    Connect
                </span>
                <span class="sr-only">
                    {{ member.firstName }} {{ member.lastName }} on LinkedIn
                </span>
            </a>
            <!-- Explicit colour: legacy `a { color: … }` rides along with app.scss. -->
            <Link
                v-if="member.profile"
                :href="route('user.giftlists', member.profile.username)"
                class="absolute right-[13px] top-[13px] text-[14px] leading-[1.3] text-white transition hover:text-white/70"
            >
                @{{ member.profile.username }}
            </Link>
        </div>

        <img
            :src="member.headshot"
            :alt="`${member.firstName} ${member.lastName}`"
            loading="lazy"
            class="relative -mt-[73px] mx-auto size-[145px] rounded-full border-[7px] border-solid border-white object-cover"
        />

        <h3
            class="mb-0 mt-1 text-center text-[22px] font-semibold leading-[1.2] text-[#102a3d]"
        >
            {{ member.firstName }} {{ member.lastName }}
        </h3>
        <p
            class="m-0 mt-1 text-center text-[16px] leading-[1.2] text-brand-tan"
        >
            {{ member.title }}
        </p>

        <p class="m-0 mt-4 px-[5px] text-[15px] leading-[19px] text-brand-navy">
            {{ member.bio }}
        </p>

        <!-- Equal columns: flex-1 let the buttons' differing padding skew them. -->
        <div
            v-if="member.profile"
            class="mt-8 grid gap-[15px] pt-2"
            :class="
                member.profile.isCurrentUser ? 'grid-cols-1' : 'grid-cols-2'
            "
        >
            <BrandButton
                class="h-[52px] w-full px-4 text-[15px]"
                @click="visitProfile"
            >
                {{ member.profile.isCurrentUser ? "View My Profile" : "Visit" }}
            </BrandButton>
            <FollowButton
                v-if="!member.profile.isCurrentUser"
                :owner="member.profile"
                compact
                class="h-[52px] w-full px-4 text-[15px]"
            />
        </div>
    </article>
</template>
